import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Check, Square, Trash2 } from "lucide-react";
import type { PaperHighlight } from "@/hooks/useHighlights";

interface HighlightOverlayProps {
  highlights: PaperHighlight[];
  selectedIds: string[];
  focusedId?: string | null;
  pageIndex: number;
  scale: number;
  pageHeight: number;
  onToggle: (highlightId: string) => void;
  onDelete?: (highlightId: string) => void;
}

interface PopoverState {
  highlightId: string;
  x: number;
  y: number;
}

export const HighlightOverlay = memo(function HighlightOverlay({
  highlights,
  selectedIds,
  focusedId,
  pageIndex,
  scale,
  pageHeight,
  onToggle,
  onDelete,
}: HighlightOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popover, setPopover] = useState<PopoverState | null>(null);

  const pageHighlights = highlights.filter((h) =>
    h.rects?.some((r) => r.pageIndex === pageIndex),
  );

  // Close popover on outside click
  useEffect(() => {
    if (!popover) return;
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopover(null);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setPopover(null);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [popover]);

  const handleRectClick = useCallback((e: React.MouseEvent, highlightId: string) => {
    e.stopPropagation();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setPopover({
      highlightId,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  if (pageHighlights.length === 0) return null;

  const activeHighlight = popover ? highlights.find((h) => h.id === popover.highlightId) : null;

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 3 }} data-highlight-overlay>
      {pageHighlights.map((highlight) => {
        const isSelected = selectedIds.includes(highlight.id);
        const isFocused = focusedId === highlight.id;
        const isImported = highlight.source === "imported";
        const isEvidence = highlight.source === "evidence";
        const rawRects = highlight.rects?.filter((r) => r.pageIndex === pageIndex && r.width > 0 && r.height > 0) ?? [];

        // Clamp overlapping rects: sort by top edge (descending PDF y = ascending screen y),
        // then trim each rect's top so it doesn't overlap the one above it on screen.
        // Only clamp when rects overlap on BOTH axes (prevents clamping side-by-side
        // rects in multi-column PDFs).
        const sorted = [...rawRects].sort((a, b) => b.y - a.y); // higher PDF y = higher on page
        const clamped = sorted.map((rect, i) => {
          if (i === 0) return rect;
          const prev = sorted[i - 1];
          // Same-line guard: pdf.js emits per-span rects for text with mid-line
          // styling changes (e.g. a year hyperlink inside body text). Those
          // rects share y and height but can have tiny floating-point x-overlap
          // (e.g. 0.2 units from sub-pixel rounding). Without this guard, the
          // vertical clamp below sees same-y same-height rects as "one full
          // line of overlap", zero-clamps the later rect, and drops it —
          // visible as a highlight that stops early before the hyperlinked
          // reference. Same-line rects should never be clamped; they're
          // adjacent runs in one line, not stacked on different lines.
          if (Math.abs(prev.y - rect.y) < 0.5) return rect;
          // Check horizontal overlap first — skip clamping for side-by-side rects
          const xOverlap = Math.min(prev.x + prev.width, rect.x + rect.width) - Math.max(prev.x, rect.x);
          if (xOverlap <= 0) return rect;
          const prevScreenBottom = (pageHeight - prev.y) * scale;
          const thisScreenTop = (pageHeight - rect.y - rect.height) * scale;
          if (thisScreenTop < prevScreenBottom) {
            const overlap = prevScreenBottom - thisScreenTop;
            const newHeight = rect.height - overlap / scale;
            return newHeight > 0 ? { ...rect, height: newHeight, y: rect.y + overlap / scale } : null;
          }
          return rect;
        }).filter(Boolean) as typeof rawRects;

        // Color scheme:
        //   in_app    → fluorescent pink (default user highlights)
        //   imported  → yellow (matches native PDF annotation color)
        //   evidence  → violet (matches the "Stash as Evidence" button theme;
        //               visually distinguishes claim-attached excerpts from
        //               personal notes)
        const baseColor = isImported
          ? "rgba(250, 204, 21,"
          : isEvidence
            ? "rgba(139, 92, 246,"
            : "rgba(255, 0, 128,";
        const solidColor = isImported
          ? "rgb(250, 204, 21)"
          : isEvidence
            ? "rgb(139, 92, 246)"
            : "rgb(255, 0, 128)";
        const bg = isFocused
          ? `${baseColor} 0.35)`
          : isSelected
            ? `${baseColor} 0.3)`
            : `${baseColor} 0.2)`;

        return clamped.map((rect, i) => (
          <div
            key={`${highlight.id}-${i}`}
            data-highlight-rect
            className={`absolute pointer-events-auto cursor-pointer ${
              isFocused ? "outline outline-2 animate-pulse" : ""
            }`}
            style={{
              left: rect.x * scale,
              top: (pageHeight - rect.y - rect.height) * scale,
              width: rect.width * scale,
              height: rect.height * scale,
              backgroundColor: bg,
              ...(isFocused ? {
                outlineColor: solidColor,
                boxShadow: `0 0 8px 2px ${baseColor} 0.5)`,
              } : {}),
            }}
            onClick={(e) => handleRectClick(e, highlight.id)}
            title={highlight.text.slice(0, 100)}
          />
        ));
      })}
      {popover && activeHighlight && (
        <div
          ref={popoverRef}
          className="absolute z-50 pointer-events-auto"
          style={{ left: popover.x, top: popover.y + 4 }}
        >
          <HighlightPopover
            highlight={activeHighlight}
            isSelected={selectedIds.includes(activeHighlight.id)}
            onToggle={() => { onToggle(activeHighlight.id); setPopover(null); }}
            onDelete={onDelete ? () => { onDelete(activeHighlight.id); setPopover(null); } : undefined}
          />
        </div>
      )}
    </div>
  );
});

interface HighlightPopoverProps {
  highlight: PaperHighlight;
  isSelected: boolean;
  onToggle: () => void;
  onDelete?: () => void;
}

function HighlightPopover({ highlight, isSelected, onToggle, onDelete }: HighlightPopoverProps) {
  return (
    <div
      className="w-56 rounded-lg border border-zinc-700 bg-zinc-900 shadow-lg overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Text preview */}
      <div className="px-3 py-2 border-b border-zinc-700">
        <p className="text-xs text-zinc-300 line-clamp-3 leading-relaxed">
          {highlight.text}
        </p>
        <span className="flex items-center gap-1.5 mt-1 text-[10px] text-zinc-500">
          {highlight.page != null && <>Page {highlight.page + 1}</>}
          {highlight.source === "imported" && (
            <span className="rounded bg-blue-500/20 px-1 text-blue-400">PDF</span>
          )}
          {highlight.source === "evidence" && (
            <span className="rounded bg-violet-500/20 px-1 text-violet-400">Evidence</span>
          )}
        </span>
      </div>

      {/* Actions */}
      <div className="p-1">
        <button
          onClick={onToggle}
          className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-zinc-800 transition-colors"
        >
          {isSelected ? (
            <>
              <Check className="size-3.5 text-amber-400" />
              <span className="text-zinc-200">Deselect for Spot Trawl</span>
            </>
          ) : (
            <>
              <Square className="size-3.5 text-zinc-500" />
              <span className="text-zinc-200">Select for Spot Trawl</span>
            </>
          )}
        </button>
        {onDelete && (
          <button
            onClick={onDelete}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="size-3.5" />
            Delete highlight
          </button>
        )}
      </div>
    </div>
  );
}
